"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrcadAliasCollisionPhysicalNameRule = void 0;
const NetNamingRules_1 = require("../../../../NetCombobulation/NetNamingRules");
class OrcadAliasCollisionPhysicalNameRule {
    constructor(reportDiagnostic) {
        this.reportDiagnostic = reportDiagnostic;
        this.type = NetNamingRules_1.NetNamingRuleType.Finalize;
    }
    apply(groups) {
        const collisions = new Map();
        const blocked = new Map();
        for (const group of groups) {
            const instances = [...group.getNets().keys()];
            const schematics = new Set(instances.map((instance) => instance.hierarchyEntry.schematic));
            const [schematic] = schematics;
            const labels = group.getNetLabels();
            const aliases = new Set(labels
                .filter((label) => label.labelType === 'alias' || label.labelType === 'off-page-connector')
                .map((label) => label.originalLabel.toUpperCase()));
            const [alias] = aliases;
            const reason = schematics.size !== 1 ? 'multiple-schematics'
                : schematic?.StoredPhysicalNetNames === undefined ? 'stored-names-absent'
                    : instances.some((instance) => instance.sheetSymbol) ? 'hierarchical-instance'
                        : (labels.some((label) => ['global', 'hierarchical-port', 'hierarchical-sheet-entry', 'virtual-bus-member'].includes(label.labelType))) ?
                            'higher-priority-carrier'
                            : aliases.size !== 1 || !alias ? 'alias-not-unique'
                                : undefined;
            if (reason !== undefined || !schematic || !alias) {
                this.reportSkip(group, reason ?? 'owner-absent');
                for (const owner of schematics) {
                    const names = blocked.get(owner) ?? new Set();
                    for (const name of aliases) {
                        names.add(name);
                    }
                    blocked.set(owner, names);
                }
                continue;
            }
            const byAlias = collisions.get(schematic) ?? new Map();
            const members = byAlias.get(alias) ?? [];
            members.push(this.member(group));
            byAlias.set(alias, members);
            collisions.set(schematic, byAlias);
        }
        for (const [schematic, byAlias] of collisions) {
            for (const [alias, members] of byAlias) {
                if (members.length < 2 || blocked.get(schematic)?.has(alias)) {
                    for (const member of members) {
                        this.reportSkip(member.group, blocked.get(schematic)?.has(alias) ? 'collision-member-ineligible' : 'no-collision');
                    }
                    continue;
                }
                const keys = members.map((member) => member.key);
                const decodedKeys = keys.filter((key) => key !== undefined);
                if (decodedKeys.length !== members.length || new Set(decodedKeys).size !== members.length) {
                    this.reportDiagnostic(`CFB physical net name [physical-name-rule-applied] approval=pending alias=${JSON.stringify(alias)} keys=${JSON.stringify(keys)} [physical-name-key-ambiguous]`);
                    for (const member of members) {
                        this.reportSkip(member.group, 'physical-name-key-ambiguous');
                    }
                    continue;
                }
                const stored = schematic.StoredPhysicalNetNames ?? [];
                const suffixed = stored.filter((name) => name.startsWith(`${alias}_`));
                if (suffixed.length !== members.length - 1 || new Set(suffixed).size !== suffixed.length) {
                    this.reportDiagnostic(`CFB physical net name [physical-name-count-mismatch] alias=${JSON.stringify(alias)} nets=${members.length - 1} stored=${JSON.stringify(suffixed)}`);
                    for (const member of members) {
                        this.reportSkip(member.group, 'physical-name-count-mismatch');
                    }
                    continue;
                }
                const candidates = members.map((member) => `${alias}_${member.key}`);
                const absent = candidates.filter((candidate) => !stored.includes(candidate));
                if (absent.length !== 1 ||
                    stored.filter((name) => name === alias).length !== 1 ||
                    suffixed.some((name) => !candidates.includes(name))) {
                    for (const member of members) {
                        this.reportSkip(member.group, 'physical-name-uncorroborated');
                    }
                    this.reportDiagnostic(`CFB physical net name [physical-name-uncorroborated] computed=${JSON.stringify(candidates)} stored=${JSON.stringify(stored)} absent=${JSON.stringify(absent)}`);
                    continue;
                }
                for (const member of members) {
                    const candidate = `${alias}_${member.key}`;
                    const computed = stored.includes(candidate) ? candidate : alias;
                    this.reportDiagnostic(`CFB physical net name [physical-name-rule-applied] approval=pending alias=${JSON.stringify(alias)} key=${member.key} computed=${JSON.stringify(computed)}`);
                    if (member.group.physicalName &&
                        member.group.physicalName !== alias &&
                        member.group.physicalName !== computed) {
                        this.reportSkip(member.group, 'existing-name-conflict');
                    }
                    else {
                        member.group.physicalName = computed;
                    }
                }
            }
        }
    }
    reportSkip(group, reason) {
        const members = [...group.getNets()].map(([instance, nets]) => ({
            path: instance.pathUniqueId,
            page: instance.hierarchyEntry.page.Name,
            wires: nets.flatMap((net) => net.wires.map((wire) => wire.strategyMetadata?.['source-wire-db-id'])),
        }));
        this.reportDiagnostic(`CFB physical net name [physical-name-rule-skipped] reason=${reason} group=${JSON.stringify(members)}`);
    }
    member(group) {
        const ids = [];
        let incomplete = false;
        for (const [instance, nets] of group.getNets()) {
            const placements = new Map((instance.hierarchyEntry.page.PartInst ?? []).map((part) => [
                `${instance.pathUniqueId}::${part.DbId}`,
                part.DbId,
            ]));
            for (const net of nets) {
                for (const wire of net.wires) {
                    const id = wire.strategyMetadata?.['source-wire-db-id'];
                    if (id === undefined || !Number.isSafeInteger(id) || id < 0) {
                        incomplete = true;
                    }
                    else {
                        ids.push(id);
                    }
                }
                for (const connection of net.connections) {
                    const id = placements.get(connection.connection.moduleRef);
                    if (id === undefined || !Number.isSafeInteger(id) || id < 0) {
                        incomplete = true;
                    }
                    else {
                        ids.push(id);
                    }
                }
            }
        }
        return { group, key: incomplete || ids.length === 0 ? undefined : Math.min(...ids) };
    }
}
exports.OrcadAliasCollisionPhysicalNameRule = OrcadAliasCollisionPhysicalNameRule;
//# sourceMappingURL=OrcadAliasCollisionPhysicalNameRule.js.map